import type { Message, AIMessage, ToolMessage } from '@langchain/langgraph-sdk'

export function getMessageText(message: Message): string {
  const { content } = message
  if (typeof content === 'string') return content
  if (Array.isArray(content)) {
    return content
      .map((block) => {
        if (typeof block === 'string') return block
        if (typeof block === 'object' && block !== null && 'text' in block) {
          return (block as { text: string }).text
        }
        return ''
      })
      .join('')
  }
  return ''
}

export type ChatItem =
  | { kind: 'text'; id: string; role: 'ai' | 'human'; text: string }
  | { kind: 'widget-response'; id: string; response: Record<string, unknown> }

export function buildChatItems(messages: Message[], completionMarker: string): ChatItem[] {
  const items: ChatItem[] = []
  const widgetToolCallIds = new Set<string>()

  for (const msg of messages) {
    if (msg.type === 'ai') {
      const aiMsg = msg as AIMessage
      for (const tc of aiMsg.tool_calls ?? []) {
        if (tc.name === 'present_widget' && tc.id) {
          widgetToolCallIds.add(tc.id)
        }
      }
      const text = getMessageText(msg)
      if (text && text !== completionMarker) {
        items.push({ kind: 'text', id: msg.id ?? '', role: 'ai', text })
      }
    } else if (msg.type === 'human') {
      const text = getMessageText(msg)
      if (text) {
        items.push({ kind: 'text', id: msg.id ?? '', role: 'human', text })
      }
    } else if (msg.type === 'tool') {
      const toolMsg = msg as ToolMessage
      if (widgetToolCallIds.has(toolMsg.tool_call_id)) {
        try {
          const parsed = JSON.parse(getMessageText(msg)) as Record<string, unknown>
          if (parsed['response'] != null) {
            items.push({
              kind: 'widget-response',
              id: msg.id ?? '',
              response: parsed['response'] as Record<string, unknown>,
            })
          }
        } catch {
          // non-JSON tool result — skip
        }
      }
    }
  }
  return items
}

export function formatWidgetResponse(response: Record<string, unknown>): string {
  if ('value' in response && typeof response['value'] === 'number') return String(response['value'])
  if ('score' in response && typeof response['score'] === 'number') return String(response['score'])
  if ('selectedOption' in response && typeof response['selectedOption'] === 'string') {
    return response['selectedOption']
  }
  if ('selectedOptions' in response && Array.isArray(response['selectedOptions'])) {
    return (response['selectedOptions'] as string[]).join(', ')
  }
  if ('orderedItems' in response && Array.isArray(response['orderedItems'])) {
    return (response['orderedItems'] as string[]).join(' \u2192 ')
  }
  return ''
}
